import {useEffect,useState} from 'react';
import {localeVersion} from '../../shared/observatory-fr';
import type {ObservatoryRelease,ObservatoryTranslationInfo} from '../../shared/observatory';
import {observatoryUiLocale,useObservatoryLocale} from './ObservatoryLocale';

type Entry={promise?:Promise<ObservatoryTranslationInfo>;info?:ObservatoryTranslationInfo;expires:number};
const translations=new Map<string,Entry>();
const keyFor=(release:ObservatoryRelease)=>`${release.sourceReleaseHash||release.id}|${localeVersion}`;
async function load(release:ObservatoryRelease):Promise<ObservatoryTranslationInfo>{
  const key=keyFor(release),cached=translations.get(key);
  if(cached?.promise)return cached.promise;
  if(cached?.info&&cached.expires>Date.now())return cached.info;
  const entry:Entry={expires:0};
  entry.promise=fetch(`/api/observatory/v1/releases/${release.id}/translations`).then(async response=>{if(!response.ok)throw new Error('Translation metadata unavailable');return response.json() as Promise<ObservatoryTranslationInfo>;}).then(info=>{
    if(info.sourceReleaseId!==release.id||release.sourceReleaseHash&&info.sourceReleaseHash!==release.sourceReleaseHash)throw new Error('Translation source mismatch');
    entry.info=info;entry.expires=Date.now()+(info.languages.fr.status==='ready'&&info.languages.fr.localeVersion===localeVersion?30_000:10_000);entry.promise=undefined;return info;
  }).catch(error=>{translations.delete(key);throw error;});
  translations.set(key,entry);if(translations.size>64)translations.delete(translations.keys().next().value!);
  return entry.promise;
}

export function useObservatoryTranslation(release:ObservatoryRelease|null){
  const {french}=useObservatoryLocale();const key=release?keyFor(release):'';
  const [state,setState]=useState<{key:string;ready:boolean;checked:boolean}>({key:'',ready:false,checked:false});
  useEffect(()=>{
    if(!french||!release)return;
    let active=true;
    const check=()=>load(release).then(info=>{if(active)setState({key,ready:info.languages.fr.status==='ready'&&info.languages.fr.localeVersion===localeVersion,checked:true});}).catch(()=>{if(active)setState({key,ready:false,checked:true});});
    void check();window.addEventListener('focus',check);return()=>{active=false;window.removeEventListener('focus',check);};
  },[french,key,release]);
  return {french,ready:french&&state.key===key&&state.ready,checked:!french||state.key===key&&state.checked};
}

export function observatoryTranslationResource(release:ObservatoryRelease,name:string){
  if(observatoryUiLocale()!=='fr-FR')return undefined;
  const info=translations.get(keyFor(release))?.info;
  return info?.languages.fr.status==='ready'&&info.languages.fr.localeVersion===localeVersion?info.languages.fr.resources.find(resource=>resource.name===name):undefined;
}
export function observatoryAsset(release:ObservatoryRelease,name:string){
  const translated=observatoryTranslationResource(release,name);
  const alias=translated?name==='report.pdf'?'report.fr.pdf':name==='report.html'?'report.fr.html':name==='cover.png'?'cover.fr.png':name.startsWith('figures/')&&name.endsWith('.svg')?name.replace(/\.svg$/,'.fr.svg'):name:name;
  return `/api/observatory/v1/releases/${release.id}/files/${alias}`;
}
